import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseInfiniteQuery, infiniteQueryOptions } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { transformedImage } from "@/lib/image-url";
import { getGalleryPage, getGalleryPageSize } from "@/lib/api/public-data.functions";
import { getPageSeo, type PageSeo } from "@/lib/api/seo.functions";
import { mergeSeo } from "@/lib/seo-head";
import { SITE } from "@/config/site";

const pageSizeQuery = {
  queryKey: ["gallery-page-size"] as const,
  queryFn: () => getGalleryPageSize(),
  staleTime: 60_000,
};

const galleryInfiniteQuery = (pageSize: number) => infiniteQueryOptions({
  queryKey: ["gallery-page", "infinite", pageSize] as const,
  queryFn: ({ pageParam }) =>
    getGalleryPage({ data: { offset: pageParam as number, limit: pageSize } }),
  initialPageParam: 0,
  getNextPageParam: (lastPage) => lastPage.nextOffset,
});

export const Route = createFileRoute("/gallery")({
  head: ({ loaderData }) => mergeSeo({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "Event Photo Gallery · Surer Adda With Sanchayeeta" },
      { name: "description", content: "Photo gallery from Sanchayeeta Seal's live performances, concerts, and events across India." },
      { property: "og:title", content: "Photo Gallery · Surer Adda" },
      { property: "og:description", content: "Photos from Sanchayeeta Seal's live concerts, weddings and cultural events." },
      { property: "og:url", content: `${SITE.url}/gallery` },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/gallery` }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ImageGallery",
        name: "Surer Adda With Sanchayeeta — Photo Gallery",
        about: { "@type": "Person", name: "Sanchayeeta Seal" },
        url: "/gallery",
      }),
    }],
  }, (loaderData as { seo?: PageSeo | null } | undefined)?.seo ?? null),
  loader: async ({ context }) => {
    const [pageSize, seo] = await Promise.all([
      context.queryClient.ensureQueryData(pageSizeQuery),
      getPageSeo({ data: { path: "/gallery" } }),
    ]);
    await context.queryClient.ensureInfiniteQueryData(galleryInfiniteQuery(pageSize));
    return { pageSize, seo };
  },
  component: GalleryPage,
  errorComponent: ({ error }) => { console.error(error); return <div className="p-10 text-center">Something went wrong loading the gallery.</div>; },
});

function GalleryPage() {
  const { pageSize } = Route.useLoaderData();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery(galleryInfiniteQuery(pageSize));
  const [lightbox, setLightbox] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const items = data.pages.flatMap((p) => p.items);
  const total = data.pages[0]?.total ?? items.length;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "600px 0px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Moments</p>
        <h1 className="mt-2 text-4xl sm:text-5xl">Photo Gallery</h1>
        {items.length === 0 ? (
          <p className="mt-10 rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground">
            Gallery images coming soon.
          </p>
        ) : (
          <>
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {items.map((g, i) => (
                <button
                  key={g.id}
                  onClick={() => setLightbox(g.image_url)}
                  className="group aspect-square overflow-hidden rounded-xl bg-card"
                  aria-label={g.caption ? `Open photo: ${g.caption}` : "Open gallery photo"}
                >
                  <ResponsiveImage
                    src={(g as any).thumbnail_url || g.image_url}
                    alt={g.caption ?? "Sanchayeeta Seal performance photo"}
                    widths={[200, 320, 480, 640, 800]}
                    sizes="(min-width: 768px) 25vw, (min-width: 640px) 33vw, 50vw"
                    fallbackWidth={480}
                    priority={i < 4}
                    className="h-full w-full object-cover group-hover:scale-105"
                  />
                </button>
              ))}
            </div>

            <div ref={sentinelRef} aria-hidden className="h-12" />

            <div className="mt-6 flex flex-col items-center gap-3">
              {isFetchingNextPage && (
                <p className="text-sm text-muted-foreground">Loading more photos…</p>
              )}
              {hasNextPage && !isFetchingNextPage && (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  className="rounded-full border border-border bg-card px-5 py-2 text-sm font-medium hover:bg-accent"
                >
                  Load more
                </button>
              )}
              {!hasNextPage && items.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Showing all {total} photo{total === 1 ? "" : "s"}.
                </p>
              )}
            </div>
          </>
        )}
      </section>
      </main>
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={() => setLightbox(null)}>
          <button aria-label="Close photo viewer" className="absolute right-4 top-4 text-white"><X className="h-8 w-8" /></button>
          <img src={transformedImage(lightbox, { width: 1600, quality: 85 })} alt="Enlarged gallery photo" className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
      <SiteFooter />
    </div>
  );
}
